import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';

const landingStatSchema = z.object({
  key: z.string(),
  value: z.number(),
  previous: z.number(),
  changePercent: z.number().nullable(),
});

const landingMonthSchema = z.object({
  month: z.string(),
  dcrCount: z.number(),
  derCount: z.number(),
  promotionCount: z.number(),
});

const landingRegionSchema = z.object({
  regionName: z.string(),
  dcrCount: z.number(),
  share: z.number(),
});

const landingRecentSchema = z.object({
  recordId: z.coerce.number(),
  employeeName: z.string().nullable(),
  regionName: z.string().nullable(),
  dcrDate: z.string(),
  approveTypeNo: z.number(),
});

export const landingDashboardSchema = z.object({
  period: z.object({ dateFrom: z.string(), dateTo: z.string() }),
  stats: z.array(landingStatSchema),
  monthly: z.array(landingMonthSchema),
  regions: z.array(landingRegionSchema),
  recent: z.array(landingRecentSchema),
});

export type LandingStat = z.infer<typeof landingStatSchema>;
export type LandingMonth = z.infer<typeof landingMonthSchema>;
export type LandingRegion = z.infer<typeof landingRegionSchema>;
export type LandingRecentDcr = z.infer<typeof landingRecentSchema>;
export type LandingDashboard = z.infer<typeof landingDashboardSchema>;

export const landingSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

export type LandingParams = z.infer<typeof landingSearchSchema>;

export const landingQueryKeys = {
  all: () => ['landing-dashboard'] as const,
  dashboard: (params: LandingParams) => [...landingQueryKeys.all(), params] as const,
};

export const landingDashboardQueryOptions = (params: LandingParams) =>
  queryOptions({
    queryKey: landingQueryKeys.dashboard(params),
    queryFn: async (): Promise<LandingDashboard> => {
      const res = await apiClient.get(API.dashboard.landing, { params });
      return landingDashboardSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });
